import { lazy, type ComponentType } from 'react';

/**
 * Every page is code-split: its JavaScript file is fetched the first time someone opens it. When
 * that file is gone, the open tab is older than the code being served — a deploy has replaced
 * every file since the tab loaded (prod answers the old name with a 404), or a dev server was
 * restarted or stopped. React.lazy on its own then throws during render and nothing catches it:
 * a white page in production, the red "Uncaught runtime errors" overlay in development.
 *
 * lazyWithRetry tries once more (a dev server mid-rebuild, a network blip), and if the file is
 * still missing it reloads the page — once — so the tab picks up the current build. The reload
 * is guarded in sessionStorage: a second failure inside the window is a real outage, and it is
 * left to the error boundary (components/ui/AppErrorBoundary) to say so instead of looping.
 */

const RELOAD_KEY = 'bearri_chunk_reload_at';
const RELOAD_WINDOW_MS = 30_000;
const RETRY_DELAY_MS = 500;

/** webpack's ChunkLoadError, mini-css-extract's CSS chunk error, and the browsers' native messages. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name = '', message = '' } = error as { name?: string; message?: string };
  return name === 'ChunkLoadError'
    || /Loading (CSS )?chunk [^\s]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message)
    || /error loading dynamically imported module/i.test(message);
}

/**
 * Reload the page to pick up the current build — at most once per window. Returns false when a
 * reload already happened moments ago (so reloading again would loop) or when storage is blocked
 * (so there is no way to know): the caller then shows an honest error instead.
 */
export function reloadForNewVersion(reload: () => void = () => window.location.reload()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
  reload?: () => void,
) {
  return lazy(async () => {
    try {
      return await load();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      try {
        // webpack forgets a failed chunk, so a second import() asks the server again
        return await load();
      } catch (again) {
        // the page is reloading: never settle, so nothing half-broken renders in the meantime
        if (isChunkLoadError(again) && reloadForNewVersion(reload)) return new Promise<never>(() => {});
        throw again;
      }
    }
  });
}
